import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import PlantillaModal from "../PlantillaModal";
import TablaProducto from "../tablas/TablaProducto";
import instanciaAxios from "../../../miscelaneo/instanciaAxios";
import { toast } from "react-toastify";

const InputProductoFacturacion = forwardRef(({
  valor = "",
  placeholder = "Referencia, código de barras o ID",
  editable = true,
  onProductoSeleccionado,
  onProductoRapido,
  onClickConProducto,
  id_sucursal = 0,
  id_lista_precio = null,
  modoFacturacion = '',
}, ref) => {
  const [inputValue, setInputValue] = useState(valor);
  const [producto, setProducto] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(valor);
  }, [valor]);

  async function ObtenerProducto(busqueda, { rapido = false } = {}) {
    try {
        if (id_sucursal == 0) {
            toast.error("Debe seleccionar una sucursal");
            return;
            
        }
      const params = {};
      if (id_lista_precio) {
        params.id_lista_precio = id_lista_precio;
      }

      const { status, data } = await instanciaAxios.get(
        `/producto/poridoreferencia/${encodeURIComponent(busqueda)}`,
        { params }
      );
      if (status === 200) {
        if (rapido) {
          // Barcode scan → auto-add +1 without modal
          if (data.matched_by === 'codigo_barra' && onProductoRapido) {
            // Pesable sin peso embebido → no auto-agregar, abrir modal para digitar/capturar peso
            if (data.es_pesable && !(data.peso_embebido > 0)) {
              setProducto(data);
              setInputValue(data.descripcion);
              onProductoSeleccionado && onProductoSeleccionado(data);
              return;
            }
            onProductoRapido(data);
            setInputValue("");
            setProducto(null);
            setTimeout(() => inputRef.current?.focus(), 50);
            return;
          }
          // Reference or ID typed → open quantity/price modal (normal flow)
        }
        // Normal flow: select product and open quantity modal
        setProducto(data);
        setInputValue(data.descripcion);
        onProductoSeleccionado && onProductoSeleccionado(data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info(`No se encontraron resultados para "${busqueda}"`);
      } else {
        console.error(error);
        toast.error("Ocurrió un error buscando el producto");
      }
    }
  }

  const manejarKeyDown = (e) => {
    if (e.key === "Enter" && editable && !producto) {
      // Si el input está vacío, abrir modal de búsqueda
      if (!inputValue || inputValue.trim() === '') {
        setMostrarModal(true);
        return;
      }
      // Enter key: backend returns matched_by to determine behavior
      // - codigo_barra → auto-add +1 (rapid)
      // - referencia/id → open quantity modal (normal)
      ObtenerProducto(inputValue, { rapido: true });
    }
  };

  const limpiarSeleccion = () => {
    setProducto(null);
    setInputValue("");
    onProductoSeleccionado && onProductoSeleccionado(null);
  };

  // Exponer funciones al padre
    useImperativeHandle(ref, () => ({
    limpiar: limpiarSeleccion,
    focus: () => inputRef.current?.focus(),
    limpiarYFocus: () => {
      limpiarSeleccion();
      // Esperar a que React re-renderice con input habilitado
      setTimeout(() => inputRef.current?.focus(), 50);
    },
    obtenerProducto: ObtenerProducto,
    productoSeleccionado: producto,
    asignarProductoManual: (productoManual) => {
        console.log("🚀 asignarProductoManual ejecutado con:", productoManual);
        setProducto(productoManual);
        setInputValue(productoManual.descripcion);
        onProductoSeleccionado && onProductoSeleccionado(productoManual);
    },
    }));
  return (
    <>
      <div
        className="w-full flex items-center p-1 rounded-xl text-gray-700 border border-gray-400 bg-white"
        onClick={() => {
          if (producto && onClickConProducto) {
            onClickConProducto(producto);
          }
        }}
        style={{ cursor: producto ? 'pointer' : 'default' }}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          disabled={!editable || !!producto}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={manejarKeyDown}
          className="w-full px-2 outline-none text-left truncate"
        />
        {producto ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              limpiarSeleccion();
            }}
            className="ml-2 text-red-500 hover:text-red-700 transition-colors"
          >
            <FaTimes />
          </button>
        ) : (
          <button
            type="button"
            disabled={!editable}
            onClick={() => setMostrarModal(true)}
            className="ml-2 text-verde-claro hover:text-color-secundario transition-colors"
          >
            <FaSearch />
          </button>
        )}
      </div>

      <PlantillaModal
        mostrar={mostrarModal}
        onClose={() => setMostrarModal(false)}
      >
        <TablaProducto
          tipo_tabla="inventarioPorSucursal"
          id_sucursal={id_sucursal}
          setProductoSeleccionado={(item) => {
            ObtenerProducto(item[0]);
            setMostrarModal(false);
          }}
          tipo_producto={modoFacturacion == 'compra' ? [1,2] : [1,2,3]}
          origen={
            modoFacturacion === 'compra'
              ? [1, 2]
              : modoFacturacion === 'venta'
              ? [1, 3]
              : [1,2,3]
          }
        />
      </PlantillaModal>
    </>
  );
});

export default InputProductoFacturacion;
